import { cn } from '@/lib/utils';
import { FC, useEffect, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useFieldArray, useForm } from 'react-hook-form';
import * as z from 'zod';
import { Form, FormControl, FormField, FormItem } from '@/components/ui/form';
import { ErrorHandle, getWordCount } from '@/lib/functions/funcUtils';
import { Label } from '@/components/ui/label';
import PrimaryButton from '@/components/ui/shared/Button/PrimaryButton/PrimaryButton';
import SecondaryButton from '@/components/ui/shared/Button/SecondaryButton/SecondaryButton';
import AdditionalDocumentCard from '../AdditionalDocumentCard/AdditionalDocumentCard';
import { DocumentType, CreatePDFPayload } from '@/lib/types/apiTypes';
import ManualSolicitationEditor, {
	ManualSolicitationState,
} from '../ManualSolicitationEditor/ManualSolicitationEditor';
import { solicitationData } from '@/constants/manualSolicitaionData';
import { useAppDispatch } from '@/lib/hooks/hooks';
import { useSelector } from 'react-redux';
import { RootState } from '@/redux/store/store';

interface FieldDetail {
	question: string;
	text: string;
	infoText?: string;
	state?: ManualSolicitationState;
}

interface FormData {
	content: FieldDetail[];
}

const formField = z.object({
	text: z.string().min(2, 'Please enter correct information'),
});

const formSchema = z.object({
	content: z.array(formField),
});

interface ManualSolicitationDetailsProps {
	submitForm: (formData: FormData, files: File[] | null) => void;
	exportDocument: (formData: FormData, type: DocumentType) => void;
}

const ManualSolicitationDetails: FC<ManualSolicitationDetailsProps> = ({
	submitForm,
	exportDocument,
}) => {
	const dispatch = useAppDispatch();
	const [activeFieldIndex, setActiveFieldIndex] = useState<number | null>(null);
	const [additionalFiles, setAdditionalFiles] = useState<File[] | null>(null);
	const exportingDocument = useSelector(
		(root: RootState) => root.manualSolicitation.exportDocument
	);

	const form = useForm<FormData>({
		resolver: zodResolver(formSchema),
		defaultValues: {},
	});

	const { fields } = useFieldArray({
		control: form.control,
		name: 'content',
	});

	const formatData = (formData): FieldDetail[] => {
		return formData.map((formItem) => {
			return {
				question: formItem.question,
				text: '',
				infoText: formItem.infoText,
				state: {
					type: 'Update',
					content: '',
				},
			};
		});
	};

	const formatPayload = (formData: FormData): CreatePDFPayload => {
		const data = solicitationData;
		const formattedData = formData.content.map((item, index) => {
			return {
				sectionName: data[index].sectionName,
				question: data[index].question,
				text: item.text,
			};
		});
		return {
			content: formattedData,
		};
	};

	const resetForm = () => {
		const formData = solicitationData;
		form.setValue('content', formatData(formData));
	};

	const onSubmit = (formData: FormData) => {
		try {
			const formattedData = formatPayload(formData);
			submitForm(formattedData, additionalFiles);
		} catch (error: unknown) {
			ErrorHandle(dispatch, error);
		}
	};

	useEffect(() => {
		if (exportingDocument) {
			const formData = form.getValues();
			const formattedData = formatPayload(formData);
			exportDocument(formattedData, exportingDocument);
		}
	}, [exportingDocument]);

	useEffect(() => {
		resetForm();
	}, []);

	return (
		<Form {...form}>
			<form
				className={cn('flex flex-col gap-3 mr-2')}
				onSubmit={form.handleSubmit(onSubmit)}
			>
				<div className="flex flex-col gap-3 p-4 bg-white 2xl:p-6 rounded-b-2xl">
					<>
						{fields.map((formItem, index) => {
							const handleStateUpdate = () => {
								if (formItem?.state) {
									formItem.state.type = 'Edit';
								}
							};
							return (
								<div className="flex flex-col" key={index}>
									<FormField
										control={form.control}
										name={`content.${index}.text`}
										render={({ field }) => {
											return (
												<div className="flex flex-col gap-3">
													<FormItem
														className={cn(
															'space-y-1 rounded-2xl hover:bg-designBackground p-4 min-h-48',
															activeFieldIndex === index
																? 'bg-designBackground border border-[#E3EEFF]'
																: 'border border-transparent'
														)}
													>
														<Label
															className={cn(
																'relative text-black font-bold dynamic-medium leading-relaxed',
																activeFieldIndex === index
																	? 'text-primary'
																	: 'text-black'
															)}
														>
															<span
																className={
																	'!absolute left-[-0.85rem] border-r-4 border-[#0000] min-w-[40px] xl:min-w-[50px] before:!h-5 before:!top-0.5 ' +
																	(activeFieldIndex === index
																		? 'border-separater-pink'
																		: 'border-separater-grey')
																}
															></span>
															<span className="inline-flex w-10">
																{index + 1}
															</span>
															{formItem.question}{' '}
															<span className="text-secondary">(required)</span>
														</Label>
														<FormControl>
															<ManualSolicitationEditor
																placeholder={formItem.infoText}
																onChange={(ev) =>
																	field.onChange(ev.currentTarget.innerText)
																}
																onFocus={() => setActiveFieldIndex(index)}
																state={formItem.state}
																onUpdateState={handleStateUpdate}
															/>
														</FormControl>
													</FormItem>
													<div className="flex items-center text-[#b4b4b4] text-base">
														<div
															className={
																'flex-1 h-[1px] mr-8 bg-[#D3DDE2] animated-container-transition '
															}
														>
															<span
																className={
																	'relative block rounded-full top-[-1.5px] w-1 h-1 bg-[#D3DDE2] animated-container-transition '
																}
															></span>
														</div>
														<div className={'flex items-center gap-2 shrink-0'}>
															<small
																className={cn(
																	'block text-end text-xs xl:text-base text-[rgba(135,133,133,0.45)]'
																)}
															>
																{getWordCount(field.value)} words
															</small>
														</div>
													</div>
												</div>
											);
										}}
									/>
								</div>
							);
						})}
					</>
				</div>
				<AdditionalDocumentCard
					onChange={(files) => setAdditionalFiles(files)}
				/>
				<div className="inline-flex gap-4 px-4 pb-10 mt-auto 2xl:px-6">
					<PrimaryButton type="submit" disabled={!form.formState.isValid}>
						Start processing this solicitation
					</PrimaryButton>
					<SecondaryButton type="reset" onClick={resetForm}>
						Clear this form
					</SecondaryButton>
				</div>
			</form>
		</Form>
	);
};

export default ManualSolicitationDetails;
